import React from 'react';
import HelpBadge from './components/HelpBadge';
import CrmTermText, { CrmTermHelp } from './CrmTermText';
import { CAMPAIGN_LAUNCH_GUIDE_HASH, PURSUIT_CRM_SPEC_URL, campaignStepTitle } from './campaignLaunchGuide';
import type { CampaignReadinessCheckId } from './campaignReadiness';
import { scrollElementWithinConsole } from './consoleScroll';

const SECTIONS: Array<{
  id: CampaignReadinessCheckId;
  extraEn: string;
  extraEs: string;
}> = [
  {
    id: 'sku',
    extraEn:
      'CompliCore, CompliAdvantage, and Enterprise are catalog products — not separate CRM workspaces. Pick one SKU per outbound campaign. Leave inbound website campaigns without a SKU; those forms do not sell a plan until convert.',
    extraEs:
      'CompliCore, CompliAdvantage y Enterprise son productos del catálogo, no espacios CRM. Un SKU por campaña saliente. Las campañas web entrantes no llevan SKU; el plan se elige al convertir.',
  },
  {
    id: 'icp',
    extraEn:
      'Write who you sell to in prose on Leads, build the ICP matrix from the web, edit it, then search for leads. Where to look is a phase (company sites, industry docs, or competitors), not a keyword phrase. Do not invent a second workspace to represent an audience.',
    extraEs:
      'Escriba a quién vende en Leads, arme la matriz ICP, revísela y luego busque leads. Dónde buscar es una fase (empresas, industria o competidores), no una frase. No cree un segundo espacio para representar una audiencia.',
  },
  {
    id: 'notes',
    extraEn:
      'The field sits under the campaign title on Home, not inside settings. It is why this book exists — separate from who you sell to (ICP). Example: inbound demos from nextcompli.com. Follow-up notes on a person stay on the lead.',
    extraEs:
      'El campo está bajo el título en Inicio, no en ajustes. Es por qué existe este libro, distinto de a quién vende (ICP). Ejemplo: demos del sitio. Las notas de una persona van en el lead.',
  },
  {
    id: 'active',
    extraEn:
      'Planned means you are still locking ICP. Active means SDRs may create leads against this SKU. Immediate next steps can Set Active and save in one click. Paused stops new motion without deleting history. Do not set Active on Advantage or Enterprise until CompliCore has a live lead-to-deal loop.',
    extraEs:
      'Planificada significa que aún cierra el ICP. Activa permite crear leads con ese SKU. Pausada detiene el movimiento sin borrar historial. No active Advantage ni Enterprise hasta que CompliCore tenga un ciclo lead-trato.',
  },
  {
    id: 'lead',
    extraEn:
      'A lead is an unqualified company + person. For outbound GTM, open Leads, describe the ICP, build and review the matrix, then search by phase: company sites, industry documentation, or competitors. Pursuit writes matching real companies with Plan equal to the Associated SKU. The contact is the target persona until you confirm a named buyer. You can still add a lead by hand. Website demo requests dual-write from public forms into the inbound campaign; convert later with a plan.',
    extraEs:
      'Un lead es empresa + persona sin calificar. En GTM saliente, abra Leads, describa el ICP, arme y revise la matriz, y busque por fase: empresas, documentación o competidores. Pursuit escribe empresas reales con Plan igual al SKU. El contacto es la persona objetivo hasta que confirme un nombre. También puede añadir un lead a mano. Las solicitudes web dual-write a la campaña entrante; el plan se elige al convertir.',
  },
  {
    id: 'deal',
    extraEn:
      'Convert on the Leads row, not from the kanban. Convert creates account, contact, and a deal whose Product is the plan you selected. Then work the card on Pipeline: next action, date, MEDDPICC. Scorecard ≥ 16 plus champion to leave Lead; economic buyer + champion to enter Proposal.',
    extraEs:
      'Convierta en la fila de Leads, no en el kanban. Convertir crea cuenta, contacto y un trato cuyo Producto es el plan elegido. Luego trabaje la tarjeta en Pipeline: siguiente acción, fecha, MEDDPICC. Scorecard ≥ 16 y campeón para salir de Lead; comprador económico + campeón para Proposal.',
  },
  {
    id: 'sequence',
    extraEn:
      'Seed already includes Discovery follow-up. Immediate next steps stay open until you enroll ICP leads in a sequence — having the template in the catalog does not count as done.',
    extraEs:
      'El catálogo ya incluye Discovery follow-up. Los próximos pasos siguen abiertos hasta que inscriba leads ICP en una secuencia; tener la plantilla no cuenta como listo.',
  },
  {
    id: 'task',
    extraEn:
      'A task is the next action on the book: call, send a one-pager, ask for the scorecard. Immediate next steps fill leftover 3/5 slots with this after launch items close.',
    extraEs:
      'Una tarea es la siguiente acción del libro. Los próximos pasos rellenan los huecos 3/5 con esto cuando cierran los ítems de lanzamiento.',
  },
  {
    id: 'website',
    extraEn:
      'Website ingest is public nextcompli.com forms dual-writing into Pursuit. It is a second channel for inbound campaigns, not a substitute for generating outbound ICP leads. Outbound GTM does not mark this step done from ingest.',
    extraEs:
      'El ingest web es el dual-write de formularios públicos. Es un segundo canal para campañas entrantes, no sustituye generar leads ICP salientes. El GTM saliente no cierra este paso por el ingest.',
  },
  {
    id: 'quote',
    extraEn:
      'After convert, put a price on the deal. Immediate next steps fill leftover 3/5 slots with the first quote so window 5 is not empty once launch is nearly done.',
    extraEs:
      'Tras convertir, ponga precio al trato. Los próximos pasos rellenan los huecos 3/5 con la primera cotización para que la ventana 5 no quede vacía.',
  },
];

const CampaignLaunchGuidePanel: React.FC<{
  spanish: boolean;
  onBack: () => void;
}> = ({ spanish, onBack }) => {
  React.useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    const node = document.getElementById(hash);
    if (node instanceof HTMLElement) {
      scrollElementWithinConsole(node);
    }
  }, []);

  return (
    <article className="pursuit-launch-guide">
      <div className="pursuit-project-toolbar">
        <h3>
          {spanish ? 'Guía de lanzamiento de campañas' : 'Campaign launch guide'}
          <HelpBadge label={spanish ? 'Acerca de esta guía' : 'About this launch guide'}>
            <p>
              {spanish
                ? 'Orden para pasar una campaña de Planificada a un trato en el tablero. La especificación Pursuit está enlazada abajo.'
                : 'The order to take a campaign from Planned to a deal on the board. The Pursuit product spec is linked below.'}
            </p>
          </HelpBadge>
        </h3>
        <button type="button" className="dashboard-btn" onClick={onBack}>
          {spanish ? 'Volver a campañas' : 'Back to campaigns'}
        </button>
      </div>

      <section>
        <h4>{spanish ? 'Qué es cada objeto' : 'What each object is'}</h4>
        <ul>
          <li>
            <strong>{spanish ? 'Espacio' : 'Workspace'}</strong>
            {spanish
              ? ' — la org que vende. Un libro protegido: NextCompli sales. No cree un espacio por plan.'
              : ' — the selling org. One protected book: NextCompli sales. Do not create a workspace per plan.'}
          </li>
          <li>
            <strong>{spanish ? 'Campaña' : 'Campaign'}</strong>
            <CrmTermText
              spanish={spanish}
              text={
                spanish
                  ? ' — un movimiento GTM: canal, audiencia y SKU opcional.'
                  : ' — one GTM motion: channel, audience, and optional SKU.'
              }
            />
          </li>
          <li>
            <strong>Lead</strong>
            <CrmTermHelp term="lead" spanish={spanish} />
            <CrmTermText
              spanish={spanish}
              text={
                spanish
                  ? ' — empresa + persona sin calificar. Plan = SKU de la campaña saliente.'
                  : ' — unqualified company + person. Plan = outbound campaign SKU.'
              }
            />
          </li>
          <li>
            <strong>{spanish ? 'Cuenta / contacto / trato' : 'Account / contact / deal'}</strong>
            <CrmTermText
              spanish={spanish}
              text={
                spanish
                  ? ' — se crean al convertir. El producto del trato es el plan (CompliCore, CompliAdvantage o Enterprise).'
                  : ' — created at convert. Product on the deal is the plan (CompliCore, CompliAdvantage, or Enterprise).'
              }
            />
          </li>
        </ul>
      </section>

      <section>
        <h4>{spanish ? 'Orden del primer ciclo' : 'First-loop order'}</h4>
        <ol>
          <li>
            <CrmTermText
              spanish={spanish}
              text={spanish ? 'Elegir CompliCore GTM en Inicio.' : 'Pick CompliCore GTM on Home.'}
            />
          </li>
          <li>
            <CrmTermText
              spanish={spanish}
              text={spanish ? 'SKU, ICP y notas → Guardar.' : 'SKU, ICP, and notes → Save.'}
            />
          </li>
          <li>{spanish ? 'Status Activa → Guardar.' : 'Status Active → Save.'}</li>
          <li>
            <CrmTermText
              spanish={spanish}
              text={spanish ? 'Nuevo lead con Plan = CompliCore.' : 'New lead with Plan = CompliCore.'}
            />
          </li>
          <li>
            <CrmTermText
              spanish={spanish}
              text={
                spanish
                  ? 'Convertir ese lead, luego trabajar el trato en Pipeline.'
                  : 'Convert that lead, then work the deal on Pipeline.'
              }
            />
          </li>
        </ol>
        <p>
          <CrmTermText
            spanish={spanish}
            text={
              spanish
                ? 'No abra Advantage ni Enterprise hasta que ese ciclo exista. El pronóstico en dólares vive en Pipeline, no en Inicio.'
                : 'Do not open Advantage or Enterprise until that loop exists. Dollar forecast lives on Pipeline, not Home.'
            }
          />
        </p>
      </section>

      {SECTIONS.map((section) => (
        <section key={section.id} id={CAMPAIGN_LAUNCH_GUIDE_HASH[section.id]}>
          <h4>
            <CrmTermText text={campaignStepTitle(section.id, spanish)} spanish={spanish} />
          </h4>
          <p>
            <CrmTermText text={spanish ? section.extraEs : section.extraEn} spanish={spanish} />
          </p>
        </section>
      ))}

      <p>
        <a href={PURSUIT_CRM_SPEC_URL} target="_blank" rel="noopener noreferrer">
          {spanish ? 'Especificación del producto Pursuit CRM (Confluence)' : 'Pursuit CRM product spec (Confluence)'}
        </a>
      </p>
    </article>
  );
};

export default CampaignLaunchGuidePanel;
